import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type {
  WeatherLayerSeries,
  WeatherLayerSeriesStats,
} from "../types/api";

type Series = WeatherLayerSeries | WeatherLayerSeriesStats;

function isZonal(s: Series): s is WeatherLayerSeriesStats {
  return "min" in s && "max" in s;
}

const VARIABLE_LABELS: Record<string, string> = {
  tmean: "Mean Temperature",
  tmax: "Max Temperature",
  tmin: "Min Temperature",
  ppt: "Precipitation",
  prec: "Precipitation",
  srad: "Solar Radiation",
  wind: "Wind Speed",
  vap: "Vapor Pressure",
  aet: "Actual Evapotranspiration",
  pet: "Potential Evapotranspiration",
  def: "Climate Water Deficit",
  q: "Runoff",
  soil: "Soil Moisture",
  swe: "Snow Water Equivalent",
};

function labelFor(code: string) {
  return VARIABLE_LABELS[code] ?? code.toUpperCase();
}

function colorFor(code: string) {
  if (/^t(mean|max|min)|temp/.test(code)) return "var(--color-mef-orange)";
  if (/ppt|prec|rain|q|aet|pet/.test(code)) return "var(--color-mef-w-rain)";
  if (/wind/.test(code)) return "var(--color-mef-gray-tint)";
  if (/srad|sun|sol/.test(code)) return "var(--color-mef-yellow)";
  return "var(--color-mef-green)";
}

function fmt(n: number | null | undefined, digits = 2) {
  return n == null ? "—" : n.toFixed(digits);
}

function gradientId(code: string) {
  return `weather-series-grad-${code}`;
}

type ChartPoint = {
  year: number;
  value: number | null;
  min?: number | null;
  max?: number | null;
};

function ChartCard({ row }: { row: Series }) {
  const zonal = isZonal(row);
  const color = colorFor(row.code);
  const unit = row.unit ?? "";

  const data: ChartPoint[] = row.per_year
    .slice()
    .sort((a, b) => a.year - b.year)
    .map((p) =>
      zonal
        ? {
            year: p.year,
            value: (p as { mean: number | null }).mean,
            min: (p as { min: number | null }).min,
            max: (p as { max: number | null }).max,
          }
        : { year: p.year, value: (p as { value: number | null }).value },
    );

  const headlineValue = row.mean;
  const headlineLabel = `${row.per_year.length}-yr mean`;

  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-3 shadow-mef-card">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-black/80 truncate">
            {labelFor(row.code)}
          </div>
          <div className="mef-small-caption font-mono">{row.code}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[15px] font-medium tabular-nums text-black/80">
            {fmt(headlineValue)}
            <span className="ml-1 text-[11px] text-black/40">{unit}</span>
          </div>
          <div className="mef-small-caption">{headlineLabel}</div>
        </div>
      </div>

      <div className="mt-2 h-28">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId(row.code)} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--color-mef-surface-2)" vertical={false} />
            <XAxis
              dataKey="year"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "rgba(0,0,0,0.4)" }}
              interval="preserveStartEnd"
              minTickGap={20}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={32}
              tick={{ fontSize: 10, fill: "rgba(0,0,0,0.4)" }}
              domain={["auto", "auto"]}
            />
            <Tooltip
              content={(p) => <TooltipBody payload={p.payload} unit={unit} zonal={zonal} />}
              cursor={{ stroke: "var(--color-mef-separator)" }}
            />
            {zonal && (
              <Area
                type="monotone"
                dataKey="max"
                stroke="none"
                fill={color}
                fillOpacity={0.12}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {zonal && (
              <Area
                type="monotone"
                dataKey="min"
                stroke="none"
                fill="#ffffff"
                fillOpacity={1}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={1.5}
              fill={`url(#${gradientId(row.code)})`}
              dot={{ r: 2.5, fill: color, stroke: "#fff", strokeWidth: 1 }}
              activeDot={{ r: 3.5, stroke: color, strokeWidth: 1.5, fill: "#fff" }}
              isAnimationActive={false}
            />
            {zonal && (
              <Line
                type="monotone"
                dataKey="min"
                stroke={color}
                strokeOpacity={0.4}
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
                isAnimationActive={false}
              />
            )}
            {zonal && (
              <Line
                type="monotone"
                dataKey="max"
                stroke={color}
                strokeOpacity={0.4}
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TooltipBody({
  payload,
  unit,
  zonal,
}: {
  payload: ReadonlyArray<{ payload?: ChartPoint }> | undefined;
  unit: string;
  zonal: boolean;
}) {
  const p = payload?.[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white px-2 py-1 shadow-mef-popup">
      <div className="text-[11px] font-medium text-black/80">{p.year}</div>
      {zonal ? (
        <div className="mt-0.5 space-y-0.5 text-[11px] text-black/60">
          <div>
            <span className="text-black/40">mean</span>{" "}
            <span className="tabular-nums text-black/80">{fmt(p.value)}</span> {unit}
          </div>
          <div>
            <span className="text-black/40">min</span>{" "}
            <span className="tabular-nums text-black/80">{fmt(p.min)}</span> {unit}
          </div>
          <div>
            <span className="text-black/40">max</span>{" "}
            <span className="tabular-nums text-black/80">{fmt(p.max)}</span> {unit}
          </div>
        </div>
      ) : (
        <div className="mt-0.5 text-[11px] tabular-nums text-black/80">
          {fmt(p.value)} {unit}
        </div>
      )}
    </div>
  );
}

/**
 * Grid of mini time-series charts — one per weather layer. Mirrors the data in
 * WeatherSeriesTable, just visualised. For zonal stats the chart shows the
 * mean line plus a translucent min/max band.
 */
export function WeatherSeriesChart({
  rows,
  columns = 2,
}: {
  rows: Series[];
  /** 1 stacks the charts at full width; 2 keeps the side-by-side grid. */
  columns?: 1 | 2;
}) {
  if (!rows.length) return null;

  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <h3 className="mef-caption">Weather — trends</h3>
      <div
        className={[
          "mt-3 grid gap-3",
          columns === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2",
        ].join(" ")}
      >
        {rows.map((r) => (
          <ChartCard key={r.code} row={r} />
        ))}
      </div>
    </div>
  );
}
